type ColorStop = [number, string] // A tuple type to represent a color stop: [position, color]

// Always returns RGBA color format with specified opacity
export const getColorForValue = (
  value: number,
  stops: ColorStop[],
  min: number,
  max: number,
  opacity: number = 0.3, // Default opacity can be adjusted
): string => {
  const pos = (value - min) / (max - min) // Normalize the value to a 0-1 scale

  if (pos < 0) return convertHexToRGBA(stops[0][1], opacity)
  if (pos > 1) return convertHexToRGBA(stops[stops.length - 1][1], opacity)

  // Find the lower and upper stops for interpolation
  const lower =
    stops
      .slice()
      .reverse()
      .find((stop) => pos >= stop[0]) || stops[0]
  const upper = stops.find((stop) => pos <= stop[0]) || stops[stops.length - 1]

  if (lower[0] === upper[0]) {
    return convertHexToRGBA(lower[1], opacity)
  }

  // Calculate the interpolation
  const range = upper[0] - lower[0]
  const rangePos = (pos - lower[0]) / range
  return colorInterpolate(lower[1], upper[1], rangePos, opacity)
}

// Interpolates two colors and returns an RGBA string
const colorInterpolate = (
  color1: string,
  color2: string,
  factor: number,
  opacity: number,
): string => {
  const hexToRgb = (hex: string): [number, number, number] => {
    return [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ]
  }

  const [r1, g1, b1] = hexToRgb(color1)
  const [r2, g2, b2] = hexToRgb(color2)
  const r = Math.round(r1 + (r2 - r1) * factor)
  const g = Math.round(g1 + (g2 - g1) * factor)
  const b = Math.round(b1 + (b2 - b1) * factor)

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

const convertHexToRGBA = (hex: string, opacity: number): string => {
  const rgb = hex.slice(1).match(/.{1,2}/g)

  // Safeguard against null values if match fails
  if (!rgb) {
    throw new Error('Invalid HEX color.')
  }

  const [r, g, b] = rgb.map((byte) => parseInt(byte, 16))
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}
